import React, {useEffect, useState} from 'react';
import {
    Box, Button, CssBaseline, TextField, Typography, Dialog,
    DialogContent, Grid, Select, MenuItem
} from '@mui/material';
import {Link, useNavigate} from 'react-router-dom';
import VOCSETLayout from '../../views/VOCSETLayout';
import {dropdownItems} from '../../views/dropdownItems';
import ProductService from '../../services/product.service';
import ExchangeService from "../../services/exchange.service";

const CreateProduct: React.FC = () => {
    const [contractCode, setContractCode] = useState('');
    const [mic, setMic] = useState('');
    const [assetClass, setAssetClass] = useState('');
    const [gmiCode, setGmiCode] = useState('');
    const [currency, setCurrency] = useState('');
    const [underlier, setUnderlier] = useState('');
    const [multiplier, setMultiplier] = useState(0);
    const [contractSize, setContractSize] = useState(0);
    const [uom, setUom] = useState('');
    const [settlementType, setSettlementType] = useState('');
    const [maturityRule, setMaturityRule] = useState('');
    const [url, setUrl] = useState('');
    const [shortDescription, setShortDescription] = useState('');
    const [description, setDescription] = useState('');
    const [error, setError] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const navigate = useNavigate();
    const [exchangeOptions, setExchangeOptions] = useState<{ mic: string; name?: string }[]>([]);

    const handleOpenDialog = () => {
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        navigate('/ProductDashboard');
    };

    useEffect(() => {
        const fetchExchanges = async () => {
            try {
                const exchanges = await ExchangeService.getExchanges(false);
                console.log(exchanges)
                setExchangeOptions(exchanges || []);
            } catch (err) {
                console.error('Failed to fetch exchanges', err);
            }
        };

        fetchExchanges();
    }, []);

    const handleSave = async () => {
        try {
            const productData = {
                contractCode,
                mic,
                assetClass,
                gmiCode,
                currency,
                underlier,
                multiplier: multiplier.toString(),
                contractSize: contractSize.toString(),
                uom,
                settlementType,
                maturityRule,
                url,
                shortDescription,
                description
            } as const;

            await ProductService.createProduct([productData]);
            handleOpenDialog();
            setTimeout(() => {
                handleCloseDialog();
            }, 2000);
        } catch (error) {
            console.error('Error creating product:', error);
            setError('Error creating product. Please try again.');
        }
    };

    const isFormValid = () => {
        return (
            contractCode !== '' &&
            mic !== '' &&
            assetClass !== '' &&
            gmiCode !== '' &&
            currency !== '' &&
            shortDescription !== '' &&
            description !== ''
        );
    };

    return (
        <div>
            <VOCSETLayout dropdownItems={dropdownItems}>
                <CssBaseline/>
                <Box
                    sx={{
                        marginTop: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                    }}
                >
                    <Typography component="h1" variant="h5" sx={{alignSelf: 'flex-start'}}>
                        Create Product
                    </Typography>
                    <Box component="form" noValidate sx={{mt: 3}}>
                        <Grid container spacing={2} sx={{paddingBottom: 4}}>
                            <Grid item xs={6}>
                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    id="contractCode"
                                    label="Contract Code"
                                    value={contractCode}
                                    onChange={(e) => setContractCode(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <Select
                                    fullWidth
                                    id="mic"
                                    required
                                    value={mic}
                                    onChange={(e) => setMic(e.target.value)}
                                    renderValue={(selected) => (selected === '' ? 'MIC' : selected)}
                                    sx={{marginTop: '16px'}}
                                    displayEmpty
                                >
                                    <MenuItem disabled value="">
                                        MIC
                                    </MenuItem>
                                    {exchangeOptions.map((exchange) => (
                                        <MenuItem key={exchange.mic} value={exchange.mic}>
                                            {exchange.mic}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Grid>
                            <Grid item xs={6}>
                                <Select
                                    fullWidth
                                    id="assetClass"
                                    value={assetClass}
                                    onChange={(e) => setAssetClass(e.target.value)}
                                    renderValue={(selected) => (selected === '' ? 'Asset Class' : selected)}
                                    sx={{marginTop: '16px'}}
                                    displayEmpty
                                >
                                    <MenuItem disabled value="">
                                        Asset Class
                                    </MenuItem>
                                    <MenuItem value="Option">Option</MenuItem>
                                    <MenuItem value="Future">Future</MenuItem>
                                </Select>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    id="gmiCode"
                                    label="GMI Code"
                                    value={gmiCode}
                                    onChange={(e) => setGmiCode(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    id="currency"
                                    label="Currency"
                                    value={currency}
                                    onChange={(e) => setCurrency(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    margin="normal"
                                    fullWidth
                                    id="underlier"
                                    label="Underlier"
                                    value={underlier}
                                    onChange={(e) => setUnderlier(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    margin="normal"
                                    fullWidth
                                    id="multiplier"
                                    label="Multiplier"
                                    type="number"
                                    value={multiplier}
                                    onChange={(e) => setMultiplier(parseInt(e.target.value, 10))}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    margin="normal"
                                    fullWidth
                                    id="contractSize"
                                    label="Contract Size"
                                    type="number"
                                    value={contractSize}
                                    onChange={(e) => setContractSize(parseInt(e.target.value, 10))}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    margin="normal"
                                    fullWidth
                                    id="uom"
                                    label="UOM"
                                    value={uom}
                                    onChange={(e) => setUom(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <Select
                                    fullWidth
                                    id="settlementType"
                                    value={settlementType}
                                    onChange={(e) => setSettlementType(e.target.value)}
                                    renderValue={(selected) => (selected === '' ? 'Settlement Type' : selected)}
                                    sx={{marginTop: '16px'}}
                                    displayEmpty
                                >
                                    <MenuItem disabled value="">
                                        Settlement Type
                                    </MenuItem>
                                    <MenuItem value="Physical">Physical</MenuItem>
                                    <MenuItem value="Cash">Cash</MenuItem>
                                </Select>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    margin="normal"
                                    fullWidth
                                    id="maturityRule"
                                    label="Maturity Rule"
                                    value={maturityRule}
                                    onChange={(e) => setMaturityRule(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    margin="normal"
                                    fullWidth
                                    id="url"
                                    label="URL"
                                    value={url}
                                    onChange={(e) => setUrl(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    id="shortDescription"
                                    label="Short Description"
                                    value={shortDescription}
                                    onChange={(e) => setShortDescription(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    id="description"
                                    label="Description"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                />
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <Button
                                onClick={handleSave}
                                variant="contained"
                                sx={{width: 'auto', mb: 2}}
                                disabled={!isFormValid()}
                            >
                                Save
                            </Button>
                            <Button
                                component={Link}
                                to="/ProductDashboard"
                                variant="contained"
                                color="primary"
                                sx={{width: 'auto', mb: 2, marginLeft: 3}}
                            >
                                Cancel
                            </Button>
                        </Grid>
                    </Box>
                    {error && (
                        <Typography variant="body2" color="error">
                            {error}
                        </Typography>
                    )}
                </Box>
            </VOCSETLayout>
            <Dialog open={openDialog} onClose={handleCloseDialog}>
                <DialogContent>
                    <Typography variant="body1">Product created successfully!</Typography>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default CreateProduct;